import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box, TextField, Button, Typography,
  CircularProgress, Alert, InputAdornment, IconButton,
} from "@mui/material";
import EmailOutlinedIcon         from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon          from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon    from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowForwardIcon          from "@mui/icons-material/ArrowForward";
import ArrowBackIcon             from "@mui/icons-material/ArrowBack";
import ShieldOutlinedIcon        from "@mui/icons-material/ShieldOutlined";

function SuperadminLogin() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = (() => {
      try { return JSON.parse(localStorage.getItem("user")); }
      catch { return null; }
    })();
    if (token && user?.role?.name === "superadmin") navigate("/superadmin");
  }, [navigate]);

  const handleLogin = async () => {
    setError(null);
    if (!email || !password) { setError("Email dan password wajib diisi."); return; }
    try {
      setLoading(true);
      const response = await api.post("/login", { email: email.trim(), password });
      const { token, user } = response.data;
      if (user?.role?.name !== "superadmin") {
        setError("Akses ditolak. Halaman ini khusus Super Administrator.");
        setLoading(false);
        return;
      }
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      navigate("/superadmin");
    } catch (err) {
      setError(err.response?.data?.message || "Email atau password salah.");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px", fontSize: "0.8rem",
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: "1.5px" },
    },
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex",
      alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{
        position: "absolute", inset: 0,
        backgroundImage: "url('https://images.unsplash.com/photo-1562774053-701939374585?w=1600&q=80')",
        backgroundSize: "cover", backgroundPosition: "center",
        filter: "blur(6px) brightness(0.35)", transform: "scale(1.05)",
      }} />
      <Box sx={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(30,41,59,0.7) 100%)",
      }} />

      {/* Card — lebih kecil */}
      <Box sx={{
        position: "relative", zIndex: 1, width: "100%", maxWidth: 340,
        backgroundColor: "rgba(255,255,255,0.97)", borderRadius: "16px",
        p: { xs: 2.5, sm: 3 },
        boxShadow: "0 20px 50px rgba(0,0,0,0.5)", mx: 2,
      }}>

        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 3 }}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA"
            sx={{ height: 28, width: "auto", objectFit: "contain" }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: "#111827", lineHeight: 1.2 }}>
              BAS-LPKIA
            </Typography>
            <Typography sx={{ fontSize: "0.58rem", color: "#6b7280", lineHeight: 1.2 }}>
              Sistem Pengadaan
            </Typography>
          </Box>
        </Box>

        {/* Kembali */}
        <Box onClick={() => navigate("/admin/login")} sx={{
          display: "flex", alignItems: "center", gap: 0.4,
          color: "#9ca3af", cursor: "pointer", mb: 2, width: "fit-content",
          "&:hover": { color: "#111827" }, transition: "color 0.15s",
        }}>
          <ArrowBackIcon sx={{ fontSize: 13 }} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Kembali</Typography>
        </Box>

        {/* Badge */}
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.5,
          backgroundColor: "#f0f9ff", border: "1px solid #bae6fd",
          borderRadius: "20px", px: 1, py: 0.3, mb: 1.5,
        }}>
          <ShieldOutlinedIcon sx={{ fontSize: 11, color: "#0369a1" }} />
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#0369a1", letterSpacing: "0.04em" }}>
            SUPER ADMINISTRATOR
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", mb: 0.3 }}>
          Masuk
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mb: 2.5 }}>
          Akses penuh sistem — hanya untuk superadmin
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, borderRadius: "7px", fontSize: "0.75rem", py: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Email */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>Email</Typography>
          <TextField fullWidth size="small" placeholder="superadmin@lpkia.ac.id"
            value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
              </InputAdornment>
            )}}
            sx={fieldSx} />
        </Box>

        {/* Password */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>Password</Typography>
          <TextField fullWidth size="small" type={showPass ? "text" : "password"}
            placeholder="••••••••" value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPass(!showPass)} edge="end">
                    {showPass
                      ? <VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                      : <VisibilityOutlinedIcon    sx={{ fontSize: 15, color: "#9ca3af" }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx} />
        </Box>

        {/* Submit */}
        <Button type="button" fullWidth variant="contained"
          onClick={handleLogin} disabled={loading}
          endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 15 }} />}
          sx={{
            borderRadius: "7px", textTransform: "none", fontSize: "0.82rem", fontWeight: 700,
            py: 1.1, backgroundColor: "#0f172a", boxShadow: "none",
            "&:hover": { backgroundColor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" },
            "&:disabled": { backgroundColor: "#e5e7eb" },
            transition: "all 0.2s",
          }}>
          {loading ? <CircularProgress size={16} sx={{ color: "#9ca3af" }} /> : "Masuk"}
        </Button>

        <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", textAlign: "center", mt: 2 }}>
          Akses terbatas — hubungi tim IT jika ada masalah
        </Typography>
      </Box>
    </Box>
  );
}

export default SuperadminLogin;